import { ApplicationConfig, inject, isDevMode, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { provideTransloco } from '@jsverse/transloco';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import localeKn from '@angular/common/locales/kn';
import localeEnIn from '@angular/common/locales/en-IN';

import { TranslocoHttpLoader } from './core/i18n/transloco-loader';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { planNoticeInterceptor } from './core/interceptors/plan-notice.interceptor';
import { LanguageService } from './core/services/language.service';
import { ThemeService } from './core/services/theme.service';
import { routes } from './app.routes';

// What `localeDate` (see shared/locale-date.pipe.ts) formats dates with —
// registered once, at the same place the app's other one-time setup lives,
// rather than on first use.
registerLocaleData(localeKn);
registerLocaleData(localeEnIn);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor, planNoticeInterceptor])),
    provideTransloco({
      config: {
        availableLangs: ['en', 'kn'],
        defaultLang: 'en',
        // English stands in wherever Kannada is not written yet, so a
        // half-translated screen reads oddly rather than showing raw keys.
        fallbackLang: 'en',
        missingHandler: { useFallbackTranslation: true },
        reRenderOnLangChange: true,
        prodMode: !isDevMode()
      },
      loader: TranslocoHttpLoader
    }),
    // ICU plurals: "1 section" / "2 sections" is a rule per language, not a
    // "(s)" glued on the end. Kannada has the same two forms as English, so
    // the same message shape serves both.
    provideTranslocoMessageformat(),
    // Build the language service once, at start-up, before anything renders.
    // Its constructor switches Transloco to the stored language; done in the
    // middle of a component's first render (where it would otherwise first be
    // asked for), an already-loaded language changes synchronously and
    // re-enters that render, which Angular cannot survive.
    provideAppInitializer(() => {
      inject(LanguageService);
      inject(ThemeService);
    })
  ]
};
